import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import { useDispatch } from "react-redux";
import { AppReviewAssignmentRepository } from "../repositories/AppReviewAssignmentRepository";
import { ErrorMessageResolver } from "../common/ErrorMessageResolver";
import ConfirmationDialog from "../common/ConfirmationDialog";
import {
  notifyShowErrorMessage,
  notifyShowSuccessMessage,
} from "../../store/AlertSlice";

export default function ReviewAssignmentPanel({ appReview, onAssigned, onBack }) {
  const [potentialAssignees, setPotentialAssignees] = useState([]);
  const [assignee, setAssignee] = useState("");
  const [saving, setSaving] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState();
  const theme = useTheme();
  const dispatch = useDispatch();

  useEffect(() => {
    if (appReview?.id) {
      getPotentialAssignees();
    }
  }, [appReview?.id]);

  const institutionLabel =
    appReview?.institutionType === "MVR"
      ? "МВР"
      : appReview?.institutionType === "JPDP"
        ? "ЈПДП"
        : "";

  const getPotentialAssignees = () => {
    AppReviewAssignmentRepository.getPotentialAssignees(appReview.id)
      .then((res) => {
        setPotentialAssignees(res.data);
      })
      .catch((err) => {
        console.error(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      });
  };

  const handleSubmitAssignee = () => {
    setSaving(true);
    AppReviewAssignmentRepository.assignReviewToUser(appReview.id, assignee?.id)
      .then(() => {
        setSaving(false);
        setAssignee("");
        onAssigned?.();
        dispatch(
          notifyShowSuccessMessage(
            "Успешно доделен предметот за обработка на корисник " +
              assignee?.firstName +
              " " +
              assignee?.lastName,
          ),
        );
      })
      .catch((err) => {
        setSaving(false);
        console.error(err);
        dispatch(notifyShowErrorMessage(ErrorMessageResolver.resolve(err)));
      });
  };

  return (
    <Grid item xs={12} md={12}>
      <Paper elevation={3} sx={{ padding: 2, mb: 2 }}>
        <Typography variant="h6" component="div">
          Доделување на предмет за обработка ({institutionLabel})
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <Typography variant="body1" component="div">
              Изберете корисник на кој ќе му биде доделен предметот за
              понатамошна обработка.
            </Typography>
          </Grid>
          {appReview?.assignedTo && (
            <Grid item xs={12} md={12}>
              <Alert severity="info" variant="filled">
                Предметот моментално е доделен на корисник{" "}
                {appReview?.assignedTo?.firstName}{" "}
                {appReview?.assignedTo?.lastName} (
                {appReview?.assignedTo?.username}). Можете да го промените
                корисникот на кој е доделен предметот со избор на друг корисник
                од листата и клик на копчето за зачувување.
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} md={5}>
            <Select
              fullWidth
              value={assignee || ""}
              onChange={(e) => setAssignee(e.target.value)}
              displayEmpty
              size="small"
            >
              <MenuItem value="Изберете корисник" disabled>
                Изберете корисник
              </MenuItem>

              {potentialAssignees?.map((user) => (
                <MenuItem
                  key={user.username}
                  value={user}
                  disabled={user?.id === appReview?.assignedTo?.id}
                >
                  {user.firstName} {user.lastName} ({user.username})
                  {user?.id === appReview?.assignedTo?.id ? " - ДОДЕЛЕН" : ""}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              size="large"
              disabled={!assignee}
              onClick={() =>
                setOpenConfirmationDialog({
                  title: "Потврда за доделување",
                  message: `Дали сте сигурни дека сакате да го доделите предметот за обработка на корисник ${assignee?.firstName} ${assignee?.lastName}?`,
                  onConfirm: () => {
                    handleSubmitAssignee();
                    setOpenConfirmationDialog(null);
                  },
                })
              }
              sx={{
                px: 3,
                py: 1,
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.primary.main,
                  color: "white",
                  cursor: "not-allowed",
                  pointerEvents: "auto",
                  opacity: 1,
                },
              }}
            >
              <Typography
                style={{
                  fontFamily: "Inter-Regular",
                  fontSize: "14px",
                  color: "#fff",
                }}
              >
                Зачувај
              </Typography>
            </Button>
            {onBack && (
              <Button
                variant="outlined"
                color="error"
                sx={{
                  ml: 1,
                }}
                onClick={onBack}
              >
                Назад
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>
      {saving && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      {openConfirmationDialog && (
        <ConfirmationDialog
          open={Boolean(openConfirmationDialog)}
          title={openConfirmationDialog.title}
          message={openConfirmationDialog.message}
          onCancel={() => setOpenConfirmationDialog()}
          onConfirm={openConfirmationDialog.onConfirm}
        />
      )}
    </Grid>
  );
}
